/* SpawnAnything — three.js meshes for the terraced terrain (see terrain.js).
   One merged vertex-coloured mesh for ground tops, cliff walls (rock strata), comic edge strips,
   map skirt and wooden bridges; one transparent animated water mesh (river, foam, waterfalls). */
import { noise2 } from './terrain.js';
import { GD_TOP, GD_WALL, GD_CLS, packCellCode } from './groundDetail.js';

export const DEFAULT_PALETTE = {
  grass: [0x8cc04a, 0xa8d25a, 0x74a944, 0xc4d273], // base, light, dark, dry patches
  sand: 0xe4d29a,      // river banks
  path: 0xdcbb7c,      // sandy roads
  pathEdge: 0xc8a96a,
  rock: [0xa08f78, 0x8a7a66, 0xb09f86, 0x7c6d5c], // cliff strata (cycled per level band)
  bed: 0x5f7058,       // river bed
  outline: 0x1e1a15,   // comic edge strips
  wood: [0xa26f3e, 0x8c5c31, 0xb07c4a],
  woodDark: 0x5c3b20,
  water: 0x45a8dc, waterDeep: 0x24709f, foam: 0xf2fbff,
};

/** Palette from a game style object (STYLES entry in main.js): ground colours + optional water/snow. */
export function paletteFromStyle(st = {}) {
  const p = { ...DEFAULT_PALETTE };
  if (Array.isArray(st.ground) && st.ground.length >= 4) p.grass = st.ground.slice(0, 4);
  if (st.water) p.water = st.water;
  if (st.snow) {
    p.rock = [0x8f9aa6, 0x7a8591, 0xa3adb8, 0x6c7681]; p.path = 0xd7dde6; p.pathEdge = 0xc3cad4; p.sand = 0xe8edf3;
    p.bed = 0x5d6b78; p.water = st.water || 0x6fa9cf;
  }
  return p;
}

// --- growable geometry buffer ---------------------------------------------------------------------
// det: per-vertex surface kind for the fine ground detail shader (src/world/groundDetail.js): QD at emit time
function makeBuf(extra, det) {
  return { p: new Float32Array(3 * 8192), n: new Float32Array(3 * 8192), c: new Float32Array(3 * 8192), x: extra ? new Float32Array(3 * 8192) : null, d: det ? new Float32Array(8192) : null, i: new Uint32Array(6 * 2048), nv: 0, ni: 0 };
}
let QD = 0; // surface kind of the quads emitted next (GD_TOP / GD_WALL, 0 = keep the vertex colour)
function grow(a, need) { if (need <= a.length) return a; let l = a.length; while (l < need) l *= 2; const b = new a.constructor(l); b.set(a); return b; }
const P = new Float64Array(12), CC = new Float32Array(12), EX = new Float32Array(12);
// quad from P (4 corners), colours CC, optional extra attribute EX; winding follows hint normal
function quad(B, hx, hy, hz, ex) {
  const v = B.nv;
  B.p = grow(B.p, (v + 4) * 3); B.n = grow(B.n, (v + 4) * 3); B.c = grow(B.c, (v + 4) * 3); B.i = grow(B.i, B.ni + 6);
  if (B.x) B.x = grow(B.x, (v + 4) * 3);
  if (B.d) { B.d = grow(B.d, v + 4); B.d[v] = B.d[v + 1] = B.d[v + 2] = B.d[v + 3] = QD; }
  const ax = P[3] - P[0], ay = P[4] - P[1], az = P[5] - P[2], bx = P[6] - P[0], by = P[7] - P[1], bz = P[8] - P[2];
  let nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx;
  const flip = nx * hx + ny * hy + nz * hz < 0;
  if (flip) { nx = -nx; ny = -ny; nz = -nz; }
  const l = Math.hypot(nx, ny, nz) || 1; nx /= l; ny /= l; nz /= l;
  if (l < 1e-9) { nx = hx; ny = hy; nz = hz; }
  for (let k = 0; k < 4; k++) {
    const o = (v + k) * 3;
    B.p[o] = P[k * 3]; B.p[o + 1] = P[k * 3 + 1]; B.p[o + 2] = P[k * 3 + 2];
    B.n[o] = nx; B.n[o + 1] = ny; B.n[o + 2] = nz;
    B.c[o] = CC[k * 3]; B.c[o + 1] = CC[k * 3 + 1]; B.c[o + 2] = CC[k * 3 + 2];
    if (B.x) { B.x[o] = ex ? EX[k * 3] : 0; B.x[o + 1] = ex ? EX[k * 3 + 1] : 0; B.x[o + 2] = ex ? EX[k * 3 + 2] : 0; }
  }
  const I = B.i, j = B.ni;
  if (!flip) { I[j] = v; I[j + 1] = v + 1; I[j + 2] = v + 2; I[j + 3] = v; I[j + 4] = v + 2; I[j + 5] = v + 3; }
  else { I[j] = v; I[j + 1] = v + 2; I[j + 2] = v + 1; I[j + 3] = v; I[j + 4] = v + 3; I[j + 5] = v + 2; }
  B.nv += 4; B.ni += 6;
}
const setP = (k, x, y, z) => { P[k * 3] = x; P[k * 3 + 1] = y; P[k * 3 + 2] = z; };
const setC = (k, c, m = 1) => { CC[k * 3] = c[0] * m; CC[k * 3 + 1] = c[1] * m; CC[k * 3 + 2] = c[2] * m; };
const setC4 = (c, m = 1) => { for (let k = 0; k < 4; k++) setC(k, c, m); };
// axis-aligned box without bottom face, shaded per face for a crisp look under any light
function box(B, x0, y0, z0, x1, y1, z1, c) {
  setP(0, x0, y1, z0); setP(1, x0, y1, z1); setP(2, x1, y1, z1); setP(3, x1, y1, z0); setC4(c, 1); quad(B, 0, 1, 0);
  setP(0, x1, y0, z0); setP(1, x1, y1, z0); setP(2, x1, y1, z1); setP(3, x1, y0, z1); setC4(c, 0.82); quad(B, 1, 0, 0);
  setP(0, x0, y0, z0); setP(1, x0, y1, z0); setP(2, x0, y1, z1); setP(3, x0, y0, z1); setC4(c, 0.74); quad(B, -1, 0, 0);
  setP(0, x0, y0, z1); setP(1, x0, y1, z1); setP(2, x1, y1, z1); setP(3, x1, y0, z1); setC4(c, 0.88); quad(B, 0, 0, 1);
  setP(0, x0, y0, z0); setP(1, x0, y1, z0); setP(2, x1, y1, z0); setP(3, x1, y0, z0); setC4(c, 0.7); quad(B, 0, 0, -1);
}
function toGeometry(THREE, B) {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(B.p.slice(0, B.nv * 3), 3));
  g.setAttribute('normal', new THREE.BufferAttribute(B.n.slice(0, B.nv * 3), 3));
  g.setAttribute('color', new THREE.BufferAttribute(B.c.slice(0, B.nv * 3), 3));
  if (B.x) g.setAttribute('aWater', new THREE.BufferAttribute(B.x.slice(0, B.nv * 3), 3));
  if (B.d) g.setAttribute('aDet', new THREE.BufferAttribute(B.d.slice(0, B.nv), 1));
  g.setIndex(new THREE.BufferAttribute(B.i.slice(0, B.ni), 1));
  g.computeBoundingBox(); g.computeBoundingSphere();
  return g;
}

// --- water material -------------------------------------------------------------------------------
function makeWaterMaterial(THREE, pal, uTime) {
  const m = new THREE.MeshLambertMaterial({ color: 0xffffff, transparent: true, opacity: 1, side: THREE.DoubleSide });
  const U = { uTime, cWater: { value: new THREE.Color(pal.water) }, cDeep: { value: new THREE.Color(pal.waterDeep) }, cFoam: { value: new THREE.Color(pal.foam) } };
  m.userData.uniforms = U;
  m.onBeforeCompile = sh => {
    Object.assign(sh.uniforms, U);
    sh.vertexShader = sh.vertexShader
      .replace('void main() {', 'attribute vec3 aWater;\nvarying vec3 vWat;\nvarying vec3 vWP;\nvoid main() {')
      .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvWP = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvWat = aWater;');
    sh.fragmentShader = sh.fragmentShader
      .replace('void main() {', `uniform float uTime; uniform vec3 cWater, cDeep, cFoam; varying vec3 vWat; varying vec3 vWP;
float wH(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float wN(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f); return mix(mix(wH(i), wH(i+vec2(1,0)), f.x), mix(wH(i+vec2(0,1)), wH(i+vec2(1,1)), f.x), f.y); }
void main() {`)
      .replace('#include <color_fragment>', `#include <color_fragment>
  vec2 wq = vWP.xz; float wt = uTime; vec3 wCol; float wA; float wFoam = 0.0;
  if (vWat.y > 0.5) {
    // waterfall sheet: bright streaks rushing down, foamier toward the bottom
    float s1 = wN(vec2((wq.x + wq.y) * 1.4, vWP.y * 1.5 + wt * 5.0));
    float s2 = wN(vec2((wq.x + wq.y) * 3.3, vWP.y * 3.2 + wt * 7.5));
    float st = smoothstep(0.42, 0.78, s1 * 0.6 + s2 * 0.4);
    wFoam = clamp(st * 0.8 + vWat.z * 0.45, 0.0, 1.0);
    wCol = mix(cWater * 1.2, cFoam, wFoam);
    wA = 0.9;
  } else {
    float n1 = wN(wq * 0.32 + vec2(-wt * 0.55, wt * 0.08));
    float n2 = wN(wq * 0.85 + vec2(-wt * 1.05, -wt * 0.25));
    float rip = sin(wq.x * 1.1 - wt * 1.7 + n1 * 4.0) * sin(wq.y * 0.9 + wt * 1.3 + n2 * 3.0);
    wCol = mix(cDeep, cWater, clamp(0.5 + 0.35 * n1 + 0.12 * rip, 0.0, 1.0));
    float f = vWat.x + (n2 - 0.5) * 0.55 + 0.12 * sin(wt * 2.2 + wq.x * 0.8 + wq.y * 0.6);
    wFoam = smoothstep(0.55, 0.72, f);
    wCol = mix(wCol, cFoam, wFoam * 0.92);
    wCol = mix(wCol, cFoam, smoothstep(0.9, 0.97, rip) * (1.0 - wFoam) * 0.35); // comic ripple lines
    wA = mix(0.8, 0.96, wFoam);
  }
  diffuseColor.rgb = wCol; diffuseColor.a *= wA;`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  float wSp = step(0.988, wH(floor(vWP.xz * 2.5) + floor(uTime * 5.0))) * (1.0 - vWat.y) * (1.0 - wFoam);
  totalEmissiveRadiance += vec3(wSp * 0.85) + diffuseColor.rgb * 0.1;`);
  };
  m.customProgramCacheKey = () => 'aw-water-1';
  return m;
}

/**
 * Build meshes for a terrain from generateTerrain(). THREE is passed in (no import) so the module
 * also works in Node tests. Options: palette (see DEFAULT_PALETTE), material (terrain, should use
 * vertexColors), waterMaterial (must read the `aWater` attribute or ignore it), uTime ({value}),
 * edgeStrips (dark comic strips along cliff tops; off when a screen-space outline pass draws the edges).
 */
export function buildTerrainMesh(t, THREE, { palette, material, waterMaterial, uTime, edgeStrips = true } = {}) {
  const pal = { ...DEFAULT_PALETTE, ...(palette || {}) };
  const lin = hex => { const c = new THREE.Color(hex); return [c.r, c.g, c.b]; };
  const C = {
    grass: pal.grass.map(lin), sand: lin(pal.sand), path: lin(pal.path), pathEdge: lin(pal.pathEdge),
    rock: pal.rock.map(lin), bed: lin(pal.bed), outline: lin(pal.outline), wood: pal.wood.map(lin), woodDark: lin(pal.woodDark),
  };
  const n = t.n, N = n * n, cell = t.cell, o = t.origin, sh = t.stepHeight, R = t.rampLen;
  const { level, water, rampDir, rampK, path, bridge, waterLevel } = t;
  const seed = t.seed | 0, bl = t.battleLevel ?? 0;
  const skirtY = -sh * 1.5 - 0.5;
  const isW = i => water[i] > 0;

  // corner heights per cell [c00, c10, c01, c11] (x index + 2*z index)
  const CH = new Float32Array(N * 4);
  for (let i = 0; i < N; i++) {
    const b = i * 4;
    if (isW(i)) { const h = waterLevel[i] * sh - t.bedDepth; CH[b] = CH[b + 1] = CH[b + 2] = CH[b + 3] = h; continue; }
    const L = level[i], rd = rampDir[i];
    if (rd < 0) { const h = L * sh; CH[b] = CH[b + 1] = CH[b + 2] = CH[b + 3] = h; continue; }
    const lo = (L + rampK[i] / R) * sh, hi = (L + (rampK[i] + 1) / R) * sh;
    // which corners sit on the high edge
    const hx1 = rd === 0, hx0 = rd === 2, hz1 = rd === 1, hz0 = rd === 3;
    CH[b] = (hx0 || hz0) ? hi : lo; CH[b + 1] = (hx1 || hz0) ? hi : lo; CH[b + 2] = (hx0 || hz1) ? hi : lo; CH[b + 3] = (hx1 || hz1) ? hi : lo;
  }
  const at = (gx, gz) => (gx < 0 || gz < 0 || gx >= n || gz >= n) ? -1 : gz * n + gx;

  // --- top colours (quantised so equal neighbours merge into long quads) ---
  const col = new Float32Array(N * 3), key = new Int32Array(N);
  // fine detail data per cell (groundDetail.js): rgb = top colour without the whole-cell edge tints (the shader
  // draws path edges and terrace rims per 0.5 m sub-voxel), a = class + open / rim / base edge bits
  const det = new Float32Array(N * 4);
  for (let gz = 0; gz < n; gz++) for (let gx = 0; gx < n; gx++) {
    const i = gz * n + gx, x = o + (gx + 0.5) * cell, z = o + (gz + 0.5) * cell;
    let c, m = 1, cls = GD_CLS.bed, dc = null, dm = 1, rimB = 0, baseB = 0;
    if (isW(i)) c = C.bed;
    else {
      let nearW = false, pathEdge = false, rim = false, base = false;
      for (let d = 0; d < 8; d++) {
        const dx = [1, 1, 0, -1, -1, -1, 0, 1][d], dz = [0, 1, 1, 1, 0, -1, -1, -1][d], j = at(gx + dx, gz + dz);
        if (j < 0) continue;
        if (isW(j) && !bridge[j]) nearW = true;
        if (d % 2 === 0) {
          if (path[i] && !path[j] && !isW(j)) pathEdge = true;
          if (rampDir[i] < 0 && rampDir[j] < 0 && !isW(j)) {
            const bit = 1 << (d >> 1); // d 0,2,4,6 → +x,+z,-x,-z
            if (level[j] < level[i]) { rim = true; rimB |= bit; } else if (level[j] > level[i]) { base = true; baseB |= bit; }
          }
        }
      }
      if (path[i]) { c = pathEdge ? C.pathEdge : C.path; dc = C.path; cls = GD_CLS.path; }
      else if (nearW) { c = C.sand; cls = GD_CLS.sand; }
      else {
        const n1 = noise2(x / 55, z / 55, seed + 101, 3), n2 = noise2(x / 14 + 13, z / 14, seed + 103, 2);
        c = C.grass[n1 > 0.7 ? 3 : n2 > 0.64 ? 2 : n1 > 0.55 ? 1 : 0];
        cls = n1 > 0.7 ? GD_CLS.dry : GD_CLS.grass;
        m *= 1 + Math.max(-2, Math.min(2, level[i] - bl)) * 0.03; // higher terraces slightly lighter
      }
      if (rampDir[i] >= 0) m *= 0.95;
      dm = m;
      if (rim) m *= 0.9; else if (base) m *= 0.86;
    }
    dc = dc || c;
    det[i * 4] = dc[0] * dm; det[i * 4 + 1] = dc[1] * dm; det[i * 4 + 2] = dc[2] * dm; det[i * 4 + 3] = cls | (rimB << 7) | (baseB << 11);
    col[i * 3] = c[0] * m; col[i * 3 + 1] = c[1] * m; col[i * 3 + 2] = c[2] * m;
    key[i] = (Math.round(col[i * 3] * 1023) << 20) | (Math.round(col[i * 3 + 1] * 1023) << 10) | Math.round(col[i * 3 + 2] * 1023);
  }

  // open edges: the neighbour's top meets this cell's top without a wall (sub-voxel colours may cross there)
  for (let gz = 0; gz < n; gz++) for (let gx = 0; gx < n; gx++) {
    const i = gz * n + gx, b = i * 4;
    let open = 0;
    for (let d = 0; d < 4; d++) {
      const j = at(gx + [1, 0, -1, 0][d], gz + [0, 1, 0, -1][d]); if (j < 0 || isW(i) !== isW(j)) continue;
      const q = j * 4;
      const ok = d === 0 ? CH[b + 1] === CH[q] && CH[b + 3] === CH[q + 2] : d === 1 ? CH[b + 2] === CH[q] && CH[b + 3] === CH[q + 1]
        : d === 2 ? CH[b] === CH[q + 1] && CH[b + 2] === CH[q + 3] : CH[b] === CH[q + 2] && CH[b + 1] === CH[q + 3];
      if (ok) open |= 1 << d;
    }
    det[b + 3] = packCellCode(det[b + 3] & 7, open, (det[b + 3] >> 7) & 15, (det[b + 3] >> 11) & 15);
  }

  const T = makeBuf(false, true);
  QD = GD_TOP;
  // --- top faces: flat cells merged in x-runs, ramp cells as sloped quads ---
  for (let gz = 0; gz < n; gz++) {
    const z0 = o + gz * cell, z1 = z0 + cell;
    let gx = 0;
    while (gx < n) {
      const i = gz * n + gx;
      if (rampDir[i] >= 0 && !isW(i)) {
        const b = i * 4, x0 = o + gx * cell, x1 = x0 + cell, cc = [col[i * 3], col[i * 3 + 1], col[i * 3 + 2]];
        setP(0, x0, CH[b], z0); setP(1, x0, CH[b + 2], z1); setP(2, x1, CH[b + 3], z1); setP(3, x1, CH[b + 1], z0); setC4(cc); quad(T, 0, 1, 0);
        gx++; continue;
      }
      const h = CH[i * 4], k = key[i];
      let e = gx + 1;
      while (e < n) { const j = gz * n + e; if ((rampDir[j] >= 0 && !isW(j)) || key[j] !== k || CH[j * 4] !== h) break; e++; }
      const cc = [col[i * 3], col[i * 3 + 1], col[i * 3 + 2]];
      setP(0, o + gx * cell, h, z0); setP(1, o + gx * cell, h, z1); setP(2, o + e * cell, h, z1); setP(3, o + e * cell, h, z0); setC4(cc); quad(T, 0, 1, 0);
      gx = e;
    }
  }

  // --- walls (cliffs, ramp sides, river banks, map skirt) with rock strata + comic edge strips ---
  const bandCol = y => C.rock[(((Math.floor(y / sh + 1e-4)) % C.rock.length) + C.rock.length) % C.rock.length];
  const OUT = 0.2; // edge strip width (m)
  // emit one wall segment; axis 0: wall in plane x = e (normal ±x, runs along z); axis 1: plane z = e
  function wall(axis, e, s0, s1, top0, top1, bot0, bot1, sgn, aIdx) {
    QD = GD_WALL;
    const nx = axis === 0 ? sgn : 0, nz = axis === 1 ? sgn : 0;
    const put = (k, s, y) => axis === 0 ? setP(k, e, y, s) : setP(k, s, y, e);
    if (top0 === top1 && bot0 === bot1) {
      // rectangular: split into strata bands at multiples of the step height
      let y = bot0, first = true;
      while (y < top0 - 1e-4) {
        let yn = (Math.floor(y / sh + 1e-4) + 1) * sh; if (yn > top0) yn = top0;
        const bc = bandCol((y + yn) * 0.5);
        put(0, s0, y); put(1, s0, yn); put(2, s1, yn); put(3, s1, y);
        const mb = first ? 0.62 : 0.8;
        setC(0, bc, mb); setC(1, bc, 1); setC(2, bc, 1); setC(3, bc, mb); quad(T, nx, 0, nz);
        y = yn; first = false;
      }
    } else {
      put(0, s0, bot0); put(1, s0, top0); put(2, s1, top1); put(3, s1, bot1);
      const ct = bandCol(Math.max(top0, top1) - 0.01), cb = bandCol(Math.min(bot0, bot1) + 0.01);
      setC(0, cb, 0.62); setC(1, ct, 1); setC(2, ct, 1); setC(3, cb, 0.62); quad(T, nx, 0, nz);
    }
    // dark strip on the higher top surface along the edge (not under water)
    if (edgeStrips && aIdx >= 0 && !isW(aIdx) && top0 - bot0 > 0.3 && top1 - bot1 > 0.3) {
      const ins = -sgn * OUT, y0 = top0 + 0.03, y1 = top1 + 0.03;
      if (axis === 0) { setP(0, e, y0, s0); setP(1, e, y1, s1); setP(2, e + ins, y1, s1); setP(3, e + ins, y0, s0); }
      else { setP(0, s0, y0, e); setP(1, s1, y1, e); setP(2, s1, y1, e + ins); setP(3, s0, y0, e + ins); }
      QD = 0; setC4(C.outline); quad(T, 0, 1, 0);
    }
  }
  // corners of cell along an edge line; side 1 = the cell's high-index side (x1 or z1)
  const edgeH = (i, axis, side, end) => i < 0 ? skirtY : CH[i * 4 + (axis === 0 ? side + 2 * end : end + 2 * side)];
  for (let axis = 0; axis < 2; axis++) {
    for (let l = -1; l < n; l++) {
      const e = o + (l + 1) * cell;
      // open runs for walls facing + (A higher) and - (B higher)
      const run = [null, null];
      const flush = r => { if (run[r]) { const q = run[r]; wall(axis, e, q.s0, q.s1, q.top, q.top, q.bot, q.bot, r === 0 ? 1 : -1, q.a); run[r] = null; } };
      for (let k = 0; k < n; k++) {
        const A = axis === 0 ? at(l, k) : at(k, l), B = axis === 0 ? at(l + 1, k) : at(k, l + 1);
        const a0 = edgeH(A, axis, 1, 0), a1 = edgeH(A, axis, 1, 1), b0 = edgeH(B, axis, 0, 0), b1 = edgeH(B, axis, 0, 1);
        const s0 = o + k * cell, s1 = s0 + cell;
        for (let r = 0; r < 2; r++) {
          const hi0 = r === 0 ? a0 : b0, hi1 = r === 0 ? a1 : b1, lo0 = r === 0 ? b0 : a0, lo1 = r === 0 ? b1 : a1, own = r === 0 ? A : B;
          if (own < 0 || !(hi0 > lo0 + 1e-4 || hi1 > lo1 + 1e-4)) { flush(r); continue; }
          const bot0 = Math.min(hi0, lo0), bot1 = Math.min(hi1, lo1);
          if (hi0 === hi1 && bot0 === bot1) {
            const q = run[r];
            if (q && q.top === hi0 && q.bot === bot0 && q.s1 === s0 && isW(q.a) === isW(own)) { q.s1 = s1; continue; }
            flush(r); run[r] = { s0, s1, top: hi0, bot: bot0, a: own };
          } else { flush(r); wall(axis, e, s0, s1, hi0, hi1, bot0, bot1, r === 0 ? 1 : -1, own); }
        }
      }
      flush(0); flush(1);
    }
  }

  // --- wooden bridges ---
  QD = 0;
  let bridgeCells = 0;
  for (let i = 0; i < N; i++) {
    if (!bridge[i]) continue;
    bridgeCells++;
    const gx = i % n, gz = (i / n) | 0, x0 = o + gx * cell, z0 = o + gz * cell, deck = level[i] * sh, bed = waterLevel[i] * sh - t.bedDepth;
    const wet = j => j >= 0 && isW(j) && !bridge[j];
    const sideX = (wet(at(gx - 1, gz)) ? 1 : 0) + (wet(at(gx + 1, gz)) ? 1 : 0);
    const sideZ = (wet(at(gx, gz - 1)) ? 1 : 0) + (wet(at(gx, gz + 1)) ? 1 : 0);
    const alongZ = sideX >= sideZ; // travel direction
    const P4 = 4, pw = cell / P4;
    for (let k = 0; k < P4; k++) {
      const wc = C.wood[(gx * 7 + gz * 13 + k * 5) % C.wood.length], y1 = deck + 0.06 + ((k + gx + gz) % 3 === 0 ? 0.03 : 0);
      if (alongZ) box(T, x0 - 0.05, deck - 0.22, z0 + k * pw + 0.03, x0 + cell + 0.05, y1, z0 + (k + 1) * pw - 0.03, wc);
      else box(T, x0 + k * pw + 0.03, deck - 0.22, z0 - 0.05, x0 + (k + 1) * pw - 0.03, y1, z0 + cell + 0.05, wc);
    }
    // railings + pillars on edges that face open water
    const sides = alongZ ? [[-1, 0], [1, 0]] : [[0, -1], [0, 1]];
    for (const [dx, dz] of sides) {
      if (!wet(at(gx + dx, gz + dz))) continue;
      const ex = dx < 0 ? x0 + 0.1 : dx > 0 ? x0 + cell - 0.1 : 0, ez = dz < 0 ? z0 + 0.1 : dz > 0 ? z0 + cell - 0.1 : 0;
      if (alongZ) {
        box(T, ex - 0.1, deck, z0 + cell / 2 - 0.1, ex + 0.1, deck + 0.95, z0 + cell / 2 + 0.1, C.woodDark);
        box(T, ex - 0.07, deck + 0.78, z0, ex + 0.07, deck + 0.9, z0 + cell, C.wood[0]);
        if (gz % 2 === 0) box(T, ex - 0.14, bed, z0 + cell / 2 - 0.14, ex + 0.14, deck - 0.2, z0 + cell / 2 + 0.14, C.woodDark);
      } else {
        box(T, x0 + cell / 2 - 0.1, deck, ez - 0.1, x0 + cell / 2 + 0.1, deck + 0.95, ez + 0.1, C.woodDark);
        box(T, x0, deck + 0.78, ez - 0.07, x0 + cell, deck + 0.9, ez + 0.07, C.wood[0]);
        if (gx % 2 === 0) box(T, x0 + cell / 2 - 0.14, bed, ez - 0.14, x0 + cell / 2 + 0.14, deck - 0.2, ez + 0.14, C.woodDark);
      }
    }
  }

  // --- water: surface, waterfall sheets, cut-away sides at the map border ---
  const Wb = makeBuf(true);
  const foamV = new Float32Array((n + 1) * (n + 1));
  for (let vz = 0; vz <= n; vz++) for (let vx = 0; vx <= n; vx++) {
    let f = 0;
    for (let k = 0; k < 4; k++) {
      const j = at(vx - 1 + (k & 1), vz - 1 + (k >> 1));
      if (j >= 0 && !isW(j)) f = 1;
      if (j >= 0 && water[j] === 2) f = Math.max(f, 0.5);
    }
    foamV[vz * (n + 1) + vx] = f;
  }
  // extra foam where a waterfall lands
  for (let i = 0; i < N; i++) {
    if (!isW(i)) continue;
    const gx = i % n, gz = (i / n) | 0;
    for (let d = 0; d < 4; d++) {
      const j = at(gx + [1, 0, -1, 0][d], gz + [0, 1, 0, -1][d]);
      if (j >= 0 && isW(j) && waterLevel[j] > waterLevel[i]) for (let k = 0; k < 4; k++) foamV[(gz + (k >> 1)) * (n + 1) + gx + (k & 1)] = 0.9;
    }
  }
  const wc = lin(pal.water), surf = i => waterLevel[i] * sh - t.waterDrop;
  for (let i = 0; i < N; i++) {
    if (!isW(i)) continue;
    const gx = i % n, gz = (i / n) | 0, x0 = o + gx * cell, z0 = o + gz * cell, y = surf(i);
    setP(0, x0, y, z0); setP(1, x0, y, z0 + cell); setP(2, x0 + cell, y, z0 + cell); setP(3, x0 + cell, y, z0);
    const fv = (vx, vz) => foamV[vz * (n + 1) + vx];
    EX.fill(0); EX[0] = fv(gx, gz); EX[3] = fv(gx, gz + 1); EX[6] = fv(gx + 1, gz + 1); EX[9] = fv(gx + 1, gz);
    setC4(wc); quad(Wb, 0, 1, 0, true);
    // waterfalls toward lower neighbours, curved outward in three rows
    for (let d = 0; d < 4; d++) {
      const dx = [1, 0, -1, 0][d], dz = [0, 1, 0, -1][d], j = at(gx + dx, gz + dz);
      if (j < 0 || !isW(j) || waterLevel[j] >= waterLevel[i]) continue;
      const yT = y + 0.04, yB = surf(j);
      const ex = dx > 0 ? x0 + cell : dx < 0 ? x0 : 0, ez = dz > 0 ? z0 + cell : dz < 0 ? z0 : 0;
      const rows = [[0, yT, 0], [0.22, yT + (yB - yT) * 0.45, 0.45], [0.5, yB - 0.02, 1]];
      for (let r = 0; r < 2; r++) {
        const [oA, yA, vA] = rows[r], [oB, yBb, vB] = rows[r + 1];
        if (dx !== 0) {
          setP(0, ex + dx * oA, yA, z0); setP(1, ex + dx * oA, yA, z0 + cell); setP(2, ex + dx * oB, yBb, z0 + cell); setP(3, ex + dx * oB, yBb, z0);
        } else {
          setP(0, x0, yA, ez + dz * oA); setP(1, x0 + cell, yA, ez + dz * oA); setP(2, x0 + cell, yBb, ez + dz * oB); setP(3, x0, yBb, ez + dz * oB);
        }
        EX[0] = vA; EX[1] = 1; EX[2] = vA; EX[3] = vA; EX[4] = 1; EX[5] = vA; EX[6] = vB; EX[7] = 1; EX[8] = vB; EX[9] = vB; EX[10] = 1; EX[11] = vB;
        setC4(wc); quad(Wb, dx, 0.3, dz, true);
      }
    }
    // border cut-away
    for (let d = 0; d < 4; d++) {
      const dx = [1, 0, -1, 0][d], dz = [0, 1, 0, -1][d];
      if (at(gx + dx, gz + dz) >= 0) continue;
      const bed = waterLevel[i] * sh - t.bedDepth;
      if (dx !== 0) { const ex = dx > 0 ? x0 + cell : x0; setP(0, ex, bed, z0); setP(1, ex, y, z0); setP(2, ex, y, z0 + cell); setP(3, ex, bed, z0 + cell); }
      else { const ez = dz > 0 ? z0 + cell : z0; setP(0, x0, bed, ez); setP(1, x0, y, ez); setP(2, x0 + cell, y, ez); setP(3, x0 + cell, bed, ez); }
      EX.fill(0); setC4(wc); quad(Wb, dx, 0, dz, true);
    }
  }

  const ut = uTime || { value: 0 };
  const ownTerrainMat = !material, ownWaterMat = !waterMaterial;
  const tMat = material || new THREE.MeshLambertMaterial({ vertexColors: true });
  const wMat = waterMaterial || makeWaterMaterial(THREE, pal, ut);
  const terrainGeo = toGeometry(THREE, T), waterGeo = toGeometry(THREE, Wb);
  const terrainMesh = new THREE.Mesh(terrainGeo, tMat);
  terrainMesh.receiveShadow = true; terrainMesh.castShadow = true; terrainMesh.name = 'terrain';
  const waterMesh = new THREE.Mesh(waterGeo, wMat);
  waterMesh.receiveShadow = true; waterMesh.renderOrder = 1; waterMesh.name = 'water';
  const group = new THREE.Group(); group.name = 'terrain-group';
  group.add(terrainMesh); if (Wb.nv) group.add(waterMesh);
  return {
    group, terrainMesh, waterMesh, uniforms: { uTime: ut },
    stats: { terrainTris: T.ni / 3, waterTris: Wb.ni / 3, bridgeCells },
    /** per-cell data for the fine ground detail shader (groundDetail.js): rgba floats, n×n, row = z */
    detail: { data: det, n, origin: o, cell, stepHeight: sh, path: C.path, pathEdge: C.pathEdge, seed },
    dispose() {
      group.removeFromParent();
      terrainGeo.dispose(); waterGeo.dispose();
      if (ownTerrainMat) tMat.dispose();
      if (ownWaterMat) wMat.dispose();
    },
  };
}
